import {
  CheckCircle,
  Eye,
  EyeOff,
  HelpCircle,
  Lock,
  LogIn,
  Unlock,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { type SheetProps, XStack, YStack } from "tamagui";
import { AccountSheetActive } from "../../plans/PlanModel";
import { Body } from "../../ui/Body";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/InputNew";
import { Pill, PillType } from "../../ui/Pill";
import { Sheet } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { Title } from "../../ui/Title";
import { accountSetPassword } from "../firebaseInit";
import { trackEvent } from "../trackEvent";

export const AccountSetPasswordSheet = observer(
  ({
    open,
    onChangeSheet,
    ...props
  }: {
    onChangeSheet?: (sheet: AccountSheetActive) => void;
  } & SheetProps) => {
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<null | Error>(null);
    const [isDone, setIsDone] = React.useState(false);
    const [showingPassword, setShowingPassword] = React.useState(false);

    const setPasswordAction = React.useCallback(async () => {
      const p = password.trim();

      const queryParameters = new URLSearchParams(window.location.search);
      const oobCode = queryParameters.get("oobCode");

      if (p) {
        setError(null);
        setLoading(true);
        try {
          oobCode && (await accountSetPassword(oobCode, p));
          setIsDone(true);
          trackEvent("account_set_password");
        } catch (e: unknown) {
          if (e instanceof Error) setError(e);
          trackEvent("account_set_password_failed");
        } finally {
          setLoading(false);
        }
      }
    }, [password]);

    return (
      <Sheet
        isLoading={loading}
        sheetId="account_password"
        open={open}
        onOpenChange={() => {
          onChangeSheet?.(AccountSheetActive.None);
        }}
        {...props}
        header={
          <YStack gap="$space.medium">
            <XStack gap="$medium" alignItems="center">
              <HelpCircle />
              <Title>Set a new password</Title>
            </XStack>
            <Subtext>Type in a new password to get going again.</Subtext>
          </YStack>
        }
      >
        <YStack gap="$space.medium">
          {error && <Pill type={PillType.Danger}>{error.message}</Pill>}

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Lock />
            <Input
              disabled={loading || isDone}
              secureTextEntry={!showingPassword}
              preventNewLine
              defaultValue={""}
              autoCorrect={false}
              icon={showingPassword ? Eye : EyeOff}
              onIconPress={() => {
                setShowingPassword(!showingPassword);
              }}
              multiline={false}
              type="border"
              placeholder={"Your new password..."}
              onChangeText={(text) => {
                setPassword(text);
              }}
              onSubmitEditing={setPasswordAction}
            />
          </XStack>

          {!isDone && (
            <Button
              isLoading={loading}
              icon={Unlock}
              onPress={setPasswordAction}
            >
              I'm ready!
            </Button>
          )}

          {isDone && (
            <XStack gap="$medium">
              <CheckCircle />
              <Body>New password saved!</Body>
            </XStack>
          )}

          {isDone && (
            <Button
              isLoading={loading}
              icon={LogIn}
              onPress={() => {
                onChangeSheet?.(AccountSheetActive.Login);
              }}
            >
              Goto Login
            </Button>
          )}
        </YStack>
      </Sheet>
    );
  },
);
